import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { useLanguage } from '../../../contexts/LanguageContext';
import { feedItemsService } from '../../../services/feedItemsService';
import { SourceReferencesSection } from '../posts/SourceReferencesSection';
import type { FeedItem } from '../../../types/feedItem';
import type { SourceReference } from '../../../types/post';
import { Logger } from '../../../utils/logger';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { Slider } from '../../ui/slider';
import {
  formatMonthYear,
  formatDateShort, // V4-CONSTANTS-AUDIT-FRONTEND 2026-05-28
  getWeekDays,
  getCalendarDays,
  isToday as isDateToday,
  isSameDay,
} from '../../../utils/dateUtils';

import { capitalizeFirstLetter, getLocaleFromLanguage } from '../../../i18n';
import type { SupportedLanguage } from '../../../i18n';
import {
  X,
  FileText,
  Rss,
  Youtube,
  Globe,
  BookOpen,
  Calendar,
  User,
  Sparkles,
  ExternalLink,
  Search,
  Info,
  Star,
  Shield,
  MessageSquareQuote,
  Target,
  Lightbulb,
  Hash,
  Languages,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  SlidersHorizontal,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface FeedItemsModalProps {
  open: boolean;
  onClose: () => void;
  /** Source ID or `web_search_{topicId}` */
  sourceId: string;
  /** Title displayed in modal header */
  title: string;
  /** Use 'source' for regular sources, 'topic' for watch topics */
  mode?: 'source' | 'topic';
  /** When provided, auto-select the item whose title matches and skip the list view */
  initialItemTitle?: string;
}

/** Map a source type string to a Lucide icon */
function getContentTypeIcon(type?: string) {
  switch (type?.toLowerCase()) {
    case 'rss':
    case 'rss_feed':
      return Rss;
    case 'youtube':
    case 'video':
      return Youtube;
    case 'website':
    case 'url':
      return Globe;
    case 'document':
    case 'pdf':
      return BookOpen;
    case 'web_search':
      return Search;
    default:
      return FileText;
  }
}

/** Convert FeedItem to SourceReference so SourceReferencesSection can render details */
function feedItemToSourceReference(item: FeedItem): SourceReference {
  return {
    id: item.id,
    title: item.title,
    url: item.url || item.link,
    type: item.contentType || 'website',
    facts: item.verifiedFacts?.map((f) => f.statement).filter((s): s is string => !!s) || [],
    aiSummary: Array.isArray(item.executiveSummary) ? item.executiveSummary.join(' ') : (item.executiveSummary || item.syntheticSummary) as string | undefined,
    postAngles: item.editorialAngles?.map((a) => a.description).filter((d): d is string => !!d) || [],
    keywords: item.keywords,
    entityNames: item.entities?.map((e) => e.name) || [],
  };
}

/** Quality score → color */
function getQualityColor(score: number): string {
  if (score >= 0.7) return '#22c55e';
  if (score >= 0.4) return '#eab308';
  return '#ef4444';
}

/** Confidence → background tint */
function getConfidenceBg(confidence: number): string {
  if (confidence >= 0.7) return 'rgba(34,197,94,0.1)';
  if (confidence >= 0.4) return 'rgba(234,179,8,0.1)';
  return 'rgba(239,68,68,0.1)';
}

/** Confidence → text color */
function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.7) return '#22c55e';
  if (confidence >= 0.4) return '#eab308';
  return '#ef4444';
}

const PAGE_SIZE = 20;

type FilterPanel = 'score' | 'date' | null;

/** Paliers alignés sur les seuils de `getQualityColor` (rouge < 40 ≤ jaune < 70 ≤ vert). */
const SCORE_PRESETS = [0, 0.4, 0.7, 0.9];

export function FeedItemsModal({ open, onClose, sourceId, title, mode = 'source', initialItemTitle }: FeedItemsModalProps) {
  const { t, language } = useLanguage();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<FeedItem | null>(null);
  const [page, setPage] = useState(0);
  const [minScore, setMinScore] = useState(0);
  const [fromDate, setFromDate] = useState('');
  // Un seul panneau de filtre ouvert à la fois : le calendrier était rendu en
  // popover absolu, donc rogné par l'`overflow-hidden` du DialogContent.
  const [openPanel, setOpenPanel] = useState<FilterPanel>(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const locale = getLocaleFromLanguage(language as SupportedLanguage);
  const hasActiveFilters = minScore > 0 || !!fromDate;

  const fetchItems = useCallback(async (pageOverride?: number) => {
    if (!sourceId) return;
    setLoading(true);
    setError(null);
    const currentPage = pageOverride ?? page;
    try {
      const options = {
        limit: PAGE_SIZE,
        from: currentPage * PAGE_SIZE,
        minScore: minScore > 0 ? minScore : undefined,
        fromDate: fromDate || undefined,
      };
      const result =
        mode === 'topic'
          ? await feedItemsService.getByTopicId(sourceId, options)
          : await feedItemsService.getBySourceId(sourceId, options);
      setItems(result.items);
      setTotal(result.total);

      // Auto-select item if initialItemTitle is provided (direct detail view, first page only)
      if (initialItemTitle && result.items.length > 0 && currentPage === 0) {
        const normalise = (s: string) => s.toLowerCase().trim();
        const match = result.items.find((item) => normalise(item.title).includes(normalise(initialItemTitle)) || normalise(initialItemTitle).includes(normalise(item.title)));
        setSelectedItem(match || result.items[0]);
      }
    } catch (err: unknown) {
      // V4-FRONT-ANY-USAGE batch 6 2026-05-29
      Logger.error('Failed to fetch feed items:', err);
      setError(extractApiErrorMessage(err, 'Failed to load items'));
    } finally {
      setLoading(false);
    }
  }, [sourceId, mode, initialItemTitle, page, minScore, fromDate]);

  useEffect(() => {
    if (open) {
      fetchItems();
      if (!initialItemTitle) {
        setSelectedItem(null);
      }
    }
  }, [open, fetchItems, initialItemTitle]);

  // Reset to page 0 when filters change
  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [minScore, fromDate, open]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchItems(newPage);
  };

  const resetFilters = () => {
    setMinScore(0);
    setFromDate('');
    setPage(0);
    setOpenPanel(null);
  };

  return (
    <>
      <Dialog open={open && !selectedItem} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className="sm:max-w-lg p-0 overflow-hidden flex flex-col"
          hideClose
          style={{
            backgroundColor: 'var(--color-background)',
            borderColor: 'var(--color-border)',
            borderRadius: '16px',
            maxHeight: 'min(75vh, 650px)',
            minHeight: '420px',
          }}
        >
          {/* Header */}
          <div
            className="px-6 pt-6 pb-4 flex-shrink-0"
            style={{
              borderBottom: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <DialogTitle
              className="flex items-center gap-3 text-sm font-medium"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                {mode === 'topic' ? (
                  <Search className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                ) : (
                  <FileText className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className="truncate block">{title}</span>
                <span className="text-xs font-normal block mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {mode === 'topic'
                    ? t.watch?.webSearchItems || 'Résultats de recherche web'
                    : t.sources?.feedItems || 'Éléments collectés'}
                </span>
              </div>
              {!loading && (
                <Badge
                  className="text-xs rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    color: 'var(--color-primary)',
                  }}
                >
                  {total}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t.sources?.feedItemsDescription || 'Liste des éléments collectés pour cette source'}
            </DialogDescription>
          </div>

          {/* Filters */}
          <div
            className="px-6 py-3 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-2">
              {/* Score */}
              <FilterPill
                icon={SlidersHorizontal}
                label={
                  minScore > 0
                    ? `${t.sources?.filterScore || 'Score'} ≥ ${Math.round(minScore * 100)}%`
                    : t.sources?.filterScore || 'Score'
                }
                active={minScore > 0}
                open={openPanel === 'score'}
                onToggle={() => setOpenPanel((p) => (p === 'score' ? null : 'score'))}
                onClear={minScore > 0 ? () => setMinScore(0) : undefined}
                clearLabel={t.common?.reset || 'Réinitialiser'}
              />

              {/* Date */}
              <FilterPill
                icon={Calendar}
                label={
                  fromDate
                    ? formatDateShort(new Date(fromDate + 'T00:00:00'))
                    : t.sources?.filterDate || 'Depuis…'
                }
                active={!!fromDate}
                open={openPanel === 'date'}
                onToggle={() => setOpenPanel((p) => (p === 'date' ? null : 'date'))}
                onClear={fromDate ? () => setFromDate('') : undefined}
                clearLabel={t.common?.reset || 'Réinitialiser'}
              />

              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="flex-shrink-0 text-xs transition-colors"
                  style={{ color: 'var(--color-text-muted)', marginLeft: 'auto' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-text-primary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-muted)')}
                >
                  {t.sources?.resetFilters || 'Tout réinitialiser'}
                </button>
              )}
            </div>

            {/* Panneau du filtre actif — rendu en flux (pas en popover) pour ne pas
                être rogné par l'overflow-hidden du DialogContent. */}
            {openPanel === 'score' && (
              <div
                className="mt-3 p-4 rounded-xl"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.sources?.filterMinScore || 'Score minimum'}
                  </span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      // `tabular-nums` est absent du CSS Tailwind figé (V4-FRONT-TAILWIND-FROZEN-CSS).
                      fontVariantNumeric: 'tabular-nums',
                      ...(minScore > 0
                        ? { backgroundColor: getConfidenceBg(minScore), color: getQualityColor(minScore) }
                        : { backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }),
                    }}
                  >
                    {minScore > 0
                      ? `≥ ${Math.round(minScore * 100)}%`
                      : t.sources?.filterAnyScore || 'Tous'}
                  </span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={[Math.round(minScore * 100)]}
                  onValueChange={([val]) => setMinScore(val / 100)}
                />
                <div className="flex items-center gap-1.5 mt-3">
                  {SCORE_PRESETS.map((preset) => {
                    const selected = Math.abs(minScore - preset) < 0.001;
                    return (
                      <button
                        key={preset}
                        onClick={() => setMinScore(preset)}
                        className="text-[10px] font-medium px-2 py-1 rounded-full border transition-colors"
                        style={{
                          // `--color-surface-hover` vaut `--color-surface` au runtime
                          // (ColorContext.tsx) : une pastille tintée serait invisible sur
                          // ce panneau, d'où le fond `background` + bordure.
                          backgroundColor: selected ? 'var(--color-primary-light)' : 'var(--color-background)',
                          borderColor: selected ? 'var(--color-primary)' : 'var(--color-border)',
                          color: selected ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                        }}
                      >
                        {preset === 0
                          ? t.sources?.filterAnyScore || 'Tous'
                          : `≥ ${Math.round(preset * 100)}%`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {openPanel === 'date' && (
              <div
                className="mt-3 rounded-xl"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <FeedDatePicker
                  locale={locale}
                  selectedDate={fromDate ? new Date(fromDate + 'T00:00:00') : null}
                  currentMonth={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  onDateSelect={(date) => {
                    setFromDate(date.toISOString().split('T')[0]);
                    setOpenPanel(null);
                  }}
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="px-6 py-4 flex items-start gap-3 animate-pulse">
                    <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-surface-hover)' }} />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded-full" style={{ width: `${60 + (i % 3) * 15}%`, backgroundColor: 'var(--color-surface-hover)' }} />
                      <div className="h-3 rounded-full" style={{ width: `${35 + (i % 2) * 20}%`, backgroundColor: 'var(--color-surface-hover)' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-16 px-6 text-center">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {error}
                </p>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
                <Info className="w-8 h-8" style={{ color: 'var(--color-text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.sources?.noItems || 'Aucun élément trouvé'}
                </p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {items.map((item) => {
                  const ContentIcon = getContentTypeIcon(item.contentType);
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedItem(item)}
                      className="w-full text-left px-6 py-4 transition-colors hover:opacity-90 cursor-pointer"
                      style={{ backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div
                          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: 'var(--color-surface-hover)' }}
                        >
                          <ContentIcon className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span
                              className="text-sm font-medium line-clamp-2 leading-snug flex-1"
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {item.title}
                            </span>
                            {/* Quality score badge */}
                            {item.qualityScore != null && item.qualityScore > 0 && (
                              <span
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 mt-0.5"
                                style={{
                                  backgroundColor: getQualityColor(item.qualityScore),
                                  color: '#fff',
                                }}
                                title={`Score: ${Math.round(item.qualityScore * 100)}%`}
                              >
                                {Math.round(item.qualityScore * 100)}%
                              </span>
                            )}
                          </div>

                          {/* Metadata row */}
                          <div className="flex items-center gap-3 flex-wrap">
                            {item.publishedAt && (
                              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                <Calendar className="w-3 h-3" />
                                {formatDateShort(item.publishedAt)}
                              </span>
                            )}
                            {item.author && (
                              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                <User className="w-3 h-3" />
                                {item.author}
                              </span>
                            )}
                            {item.aiAnalyzed && (
                              <span
                                className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                                style={{
                                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                  color: 'var(--color-primary)',
                                }}
                              >
                                <Sparkles className="w-3 h-3" />
                                IA
                              </span>
                            )}
                          </div>

                          {/* AI summary or description fallback */}
                          {(item.executiveSummary || item.description) && (
                            <p
                              className="text-xs mt-1.5 line-clamp-2 leading-relaxed"
                              style={{ color: 'var(--color-text-secondary)' }}
                            >
                              {item.executiveSummary || item.description}
                            </p>
                          )}
                        </div>

                        {/* External link indicator */}
                        {(item.url || item.link) && (
                          <ExternalLink
                            className="w-3.5 h-3.5 flex-shrink-0 mt-1"
                            style={{ color: 'var(--color-text-muted)' }}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination footer */}
          <div
            className="px-6 py-3 flex items-center justify-between flex-shrink-0"
            style={{
              borderTop: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0 || loading}
                onClick={() => handlePageChange(page - 1)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                style={{ backgroundColor: 'var(--color-surface-elevated)' }}
              >
                ←
              </button>
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {total > 0 ? `${page + 1} / ${totalPages} · ${total}` : '—'}
              </span>
              <button
                disabled={(page + 1) * PAGE_SIZE >= total || loading}
                onClick={() => handlePageChange(page + 1)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                style={{ backgroundColor: 'var(--color-surface-elevated)' }}
              >
                →
              </button>
            </div>
            <AnimatedIconButton icon={X} label={t.common?.close || 'Fermer'} onClick={onClose} variant="secondary" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Item detail — rendered as SourceReferencesSection modal */}
      {selectedItem && (
        <SourceReferencesSection
          sources={[feedItemToSourceReference(selectedItem)]}
          className="hidden"
        />
      )}

      {/* Dedicated item detail modal */}
      {selectedItem && (
        <Dialog open={!!selectedItem} onOpenChange={(o) => { if (!o) { initialItemTitle ? onClose() : setSelectedItem(null); } }}>
          <DialogContent
            className="sm:max-w-lg p-0 overflow-hidden flex flex-col"
            hideClose
            style={{
              backgroundColor: 'var(--color-background)',
              borderColor: 'var(--color-border)',
              borderRadius: '16px',
              maxHeight: 'min(85vh, 750px)',
            }}
          >
            {/* Header */}
            <div
              className="px-6 pt-6 pb-4 flex-shrink-0"
              style={{
                borderBottom: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface)',
              }}
            >
              <DialogTitle className="flex items-center gap-3 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                  {(() => {
                    const Icon = getContentTypeIcon(selectedItem.contentType);
                    return <Icon className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />;
                  })()}
                </div>
                {t.sources?.sourceDetails || 'Détails de la source'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t.sources?.sourceDetails || 'Détails de la source'}
              </DialogDescription>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-6 flex-1 overflow-y-auto min-h-0">
              {/* Title */}
              <h3 className="text-base font-semibold leading-snug" style={{ color: 'var(--color-text-primary)' }}>
                {selectedItem.title}
              </h3>

              {/* Metadata row */}
              <div className="flex items-center gap-3 flex-wrap">
                {selectedItem.publishedAt && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" style={{ color: 'var(--color-text-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {formatDateShort(selectedItem.publishedAt)}
                    </span>
                  </div>
                )}
                {selectedItem.author && (
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" style={{ color: 'var(--color-text-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {selectedItem.author}
                    </span>
                  </div>
                )}
                {selectedItem.language && (
                  <div className="flex items-center gap-1.5">
                    <Languages className="w-3.5 h-3.5" style={{ color: 'var(--color-text-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {selectedItem.language.toUpperCase()}
                    </span>
                  </div>
                )}
                {selectedItem.contentType && (
                  <span
                    className="text-xs px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'var(--color-surface-hover)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {selectedItem.contentType}
                  </span>
                )}
                {selectedItem.aiAnalyzed && (
                  <span
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      color: 'var(--color-primary)',
                    }}
                  >
                    <Sparkles className="w-3 h-3" />
                    {t.sources?.aiAnalyzed || 'Analysé par IA'}
                  </span>
                )}
              </div>

              {/* Quality Score */}
              {selectedItem.qualityScore !== undefined && selectedItem.qualityScore !== null && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" style={{ color: getQualityColor(selectedItem.qualityScore) }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.qualityScore || 'Score de qualité'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <div
                      className="h-2 rounded-full flex-1"
                      style={{ backgroundColor: 'var(--color-surface-hover)', maxWidth: '120px' }}
                    >
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${Math.round(selectedItem.qualityScore * 100)}%`,
                          backgroundColor: getQualityColor(selectedItem.qualityScore),
                        }}
                      />
                    </div>
                    <span
                      className="text-xs font-semibold tabular-nums"
                      style={{ color: getQualityColor(selectedItem.qualityScore) }}
                    >
                      {Math.round(selectedItem.qualityScore * 100)}%
                    </span>
                  </div>
                </div>
              )}

              {/* URL */}
              {(selectedItem.url || selectedItem.link) && (
                <a
                  href={selectedItem.url || selectedItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs transition-colors hover:underline"
                  style={{ color: 'var(--color-primary)' }}
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="truncate max-w-[350px]">{selectedItem.url || selectedItem.link}</span>
                </a>
              )}

              {/* Description */}
              {selectedItem.description && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {selectedItem.description}
                  </p>
                </div>
              )}

              {/* Executive Summary */}
              {selectedItem.executiveSummary && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--color-primary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.aiSummary || 'Résumé IA'}
                    </span>
                  </div>
                  <div
                    className="p-4 rounded-xl text-sm leading-relaxed"
                    style={{
                      backgroundColor: 'rgba(99, 102, 241, 0.06)',
                      color: 'var(--color-text-primary)',
                      border: '1px solid rgba(99, 102, 241, 0.12)',
                    }}
                  >
                    {selectedItem.executiveSummary}
                  </div>
                </div>
              )}

              {/* Synthetic Summary (bullet points) */}
              {selectedItem.syntheticSummary && selectedItem.syntheticSummary.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <FileText className="w-3.5 h-3.5" style={{ color: 'var(--color-primary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.keyPoints || 'Points clés'}
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {selectedItem.syntheticSummary.map((point, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm leading-relaxed"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        <span
                          className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2"
                          style={{ backgroundColor: 'var(--color-primary)' }}
                        />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Viewpoints */}
              {selectedItem.viewpoints && selectedItem.viewpoints.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <MessageSquareQuote className="w-3.5 h-3.5" style={{ color: 'var(--color-info)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.viewpoints || 'Points de vue'}
                    </span>
                    <Badge
                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }}
                    >
                      {selectedItem.viewpoints.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {selectedItem.viewpoints.map((vp, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg text-sm"
                        style={{
                          backgroundColor: 'var(--color-surface)',
                          border: '1px solid var(--color-border)',
                        }}
                      >
                        {vp.author && (
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <User className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
                            <span className="text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {vp.author}
                            </span>
                            {vp.confidence !== undefined && (
                              <span className="text-[10px] ml-auto tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                                {Math.round(vp.confidence * 100)}% {t.sources?.confidence || 'confiance'}
                              </span>
                            )}
                          </div>
                        )}
                        {vp.position && (
                          <p className="text-sm leading-relaxed mb-1" style={{ color: 'var(--color-text-primary)' }}>
                            {vp.position}
                          </p>
                        )}
                        {vp.citation && (
                          <blockquote
                            className="text-xs italic pl-3 mt-1.5"
                            style={{
                              borderLeft: '2px solid var(--color-primary)',
                              color: 'var(--color-text-secondary)',
                            }}
                          >
                            "{vp.citation}"
                          </blockquote>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Editorial Angles (enriched) */}
              {selectedItem.editorialAngles && selectedItem.editorialAngles.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Lightbulb className="w-3.5 h-3.5" style={{ color: 'var(--color-warning)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.postAngles || 'Angles éditoriaux'}
                    </span>
                    <Badge
                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }}
                    >
                      {selectedItem.editorialAngles.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {selectedItem.editorialAngles.map((angle, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg text-sm"
                        style={{
                          backgroundColor: 'rgba(234, 179, 8, 0.04)',
                          border: '1px solid rgba(234, 179, 8, 0.15)',
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          {angle.type && (
                            <span
                              className="text-[10px] font-medium uppercase px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: 'rgba(234, 179, 8, 0.15)',
                                color: 'var(--color-warning)',
                              }}
                            >
                              {angle.type}
                            </span>
                          )}
                          {angle.targetAudience && (
                            <span className="text-[10px] ml-auto" style={{ color: 'var(--color-text-muted)' }}>
                              {t.sources?.targetAudience || 'Cible'} : {angle.targetAudience}
                            </span>
                          )}
                        </div>
                        {angle.description && (
                          <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-primary)' }}>
                            {angle.description}
                          </p>
                        )}
                        {angle.hookSuggestion && (
                          <p className="text-xs mt-1.5 italic" style={{ color: 'var(--color-text-secondary)' }}>
                            💡 {angle.hookSuggestion}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Verified Facts (enriched with confidence) */}
              {selectedItem.verifiedFacts && selectedItem.verifiedFacts.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Target className="w-3.5 h-3.5" style={{ color: 'var(--color-success)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.keyFacts || 'Faits clés extraits'}
                    </span>
                    <Badge
                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }}
                    >
                      {selectedItem.verifiedFacts.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {selectedItem.verifiedFacts.map((fact, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg text-sm"
                        style={{
                          backgroundColor: 'var(--color-surface)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid var(--color-border)',
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5"
                            style={{
                              backgroundColor: 'var(--color-primary-light)',
                              color: 'var(--color-primary)',
                            }}
                          >
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="leading-relaxed">{fact.statement}</span>
                            <div className="flex items-center gap-3 mt-1.5">
                              {fact.sourceContext && (
                                <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                                  {t.sources?.sourceContext || 'Source'} : {fact.sourceContext}
                                </span>
                              )}
                              {fact.confidence !== undefined && (
                                <span
                                  className="text-[10px] ml-auto tabular-nums px-1.5 py-0.5 rounded"
                                  style={{
                                    backgroundColor: getConfidenceBg(fact.confidence),
                                    color: getConfidenceColor(fact.confidence),
                                  }}
                                >
                                  {Math.round(fact.confidence * 100)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Entities (enriched with type & role) */}
              {selectedItem.entities && selectedItem.entities.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <User className="w-3.5 h-3.5" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.entities || 'Entités mentionnées'}
                    </span>
                    <Badge
                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }}
                    >
                      {selectedItem.entities.length}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedItem.entities.map((entity, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg"
                        style={{
                          backgroundColor: 'var(--color-surface-hover)',
                          border: '1px solid var(--color-border)',
                        }}
                        title={entity.context || undefined}
                      >
                        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {entity.name}
                        </span>
                        {entity.type && (
                          <span
                            className="text-[10px] px-1 py-0.5 rounded"
                            style={{
                              backgroundColor: 'var(--color-primary-light)',
                              color: 'var(--color-primary)',
                            }}
                          >
                            {entity.type}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks & Guardrails */}
              {selectedItem.risksGuardrails && selectedItem.risksGuardrails.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--color-danger)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.risksGuardrails || 'Risques & garde-fous'}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {selectedItem.risksGuardrails.map((risk, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 text-sm p-2.5 rounded-lg"
                        style={{
                          backgroundColor: 'rgba(239, 68, 68, 0.04)',
                          border: '1px solid rgba(239, 68, 68, 0.12)',
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-1" style={{ color: 'var(--color-danger)' }} />
                        <span className="leading-relaxed">{risk}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {selectedItem.keywords && selectedItem.keywords.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Hash className="w-3.5 h-3.5" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.extractedKeywords || 'Mots-clés extraits'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedItem.keywords.map((kw, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: 'var(--color-primary-light)',
                          color: 'var(--color-primary)',
                        }}
                      >
                        #{kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="px-6 py-4 flex justify-end flex-shrink-0"
              style={{
                borderTop: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface)',
              }}
            >
              <AnimatedIconButton
                icon={X}
                label={t.common?.close || 'Fermer'}
                onClick={() => initialItemTitle ? onClose() : setSelectedItem(null)}
                variant="secondary"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ─── Filter pill ──────────────────────────────────────────────────────────────

interface FilterPillProps {
  icon: LucideIcon;
  label: string;
  /** Le filtre porte une valeur (teinte primaire) */
  active: boolean;
  /** Le panneau associé est déployé */
  open: boolean;
  onToggle: () => void;
  /** Fourni seulement quand le filtre est actif : efface cette valeur seule */
  onClear?: () => void;
  clearLabel: string;
}

function FilterPill({ icon: Icon, label, active, open, onToggle, onClear, clearLabel }: FilterPillProps) {
  const tint = active
    ? { bg: 'var(--color-primary-light)', border: 'var(--color-primary)', fg: 'var(--color-primary)' }
    : open
    ? { bg: 'var(--color-surface-hover)', border: 'var(--color-border)', fg: 'var(--color-text-primary)' }
    : { bg: 'var(--color-background)', border: 'var(--color-border)', fg: 'var(--color-text-secondary)' };

  return (
    <div
      className="flex items-center rounded-full border transition-colors flex-shrink-0"
      style={{ backgroundColor: tint.bg, borderColor: tint.border, color: tint.fg }}
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-xs"
        style={{
          color: 'inherit',
          padding: onClear ? '6px 6px 6px 12px' : '6px 12px',
        }}
      >
        <Icon className="w-3 h-3" />
        {label}
        <ChevronDown
          className="w-3 h-3 transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'none', opacity: 0.6 }}
        />
      </button>
      {onClear && (
        <button
          onClick={onClear}
          title={clearLabel}
          aria-label={clearLabel}
          className="flex items-center"
          style={{ color: 'inherit', padding: '6px 10px 6px 2px' }}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ─── Inline calendar for FeedItemsModal ───────────────────────────────────────

interface FeedDatePickerProps {
  selectedDate: Date | null;
  currentMonth: Date;
  locale: string;
  onMonthChange: (month: Date) => void;
  onDateSelect: (date: Date) => void;
}

function FeedDatePicker({ selectedDate, currentMonth, locale, onMonthChange, onDateSelect }: FeedDatePickerProps) {
  const days = getCalendarDays(currentMonth);
  const weekDays = getWeekDays(locale);

  const previousMonth = () => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  return (
    <div className="p-3">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={previousMonth}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <ChevronLeft className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
        </button>
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {capitalizeFirstLetter(formatMonthYear(currentMonth, locale))}
        </span>
        <button
          onClick={nextMonth}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
        </button>
      </div>

      {/* Week day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day, i) => (
          <div key={i} className="text-xs text-center font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map(({ date, isCurrentMonth }, index) => {
          const today = isDateToday(date);
          const selected = selectedDate ? isSameDay(date, selectedDate) : false;

          return (
            <button
              key={index}
              onClick={() => isCurrentMonth && onDateSelect(date)}
              disabled={!isCurrentMonth}
              className={`h-9 w-full rounded-md flex items-center justify-center text-sm transition-colors ${!isCurrentMonth ? 'opacity-30' : ''}`}
              style={{
                backgroundColor: !isCurrentMonth
                  ? 'transparent'
                  : selected
                  ? 'var(--color-primary)'
                  : today
                  ? 'var(--color-surface)'
                  : 'transparent',
                color: !isCurrentMonth
                  ? 'var(--color-text-secondary)'
                  : selected
                  ? 'var(--color-text-light)'
                  : today
                  ? 'var(--color-primary)'
                  : 'var(--color-text-primary)',
                borderColor: today && !selected ? 'var(--color-primary)' : 'transparent',
                borderWidth: today && !selected ? '2px' : '1px',
                borderStyle: 'solid',
                cursor: !isCurrentMonth ? 'not-allowed' : 'pointer',
                fontWeight: selected || today ? 600 : 400,
              }}
              onMouseEnter={(e) => {
                if (isCurrentMonth && !selected) {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }
              }}
              onMouseLeave={(e) => {
                if (isCurrentMonth && !selected) {
                  e.currentTarget.style.backgroundColor = today ? 'var(--color-surface)' : 'transparent';
                }
              }}
            >
              <span>{date.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
